/**
 * Sort model for the search results table.
 *
 * SORTING IS ENTIRELY SERVER-SIDE. The table sends at most one
 * `(sortBy, sortOrder)` pair with the search request and renders the rows in
 * exactly the order the backend returned them. There is no client-side
 * comparator, and there must never be one again — see "Why the client layer was
 * removed" below.
 *
 * Shape: at most a SINGLE criterion.
 *
 *   { field: 'NAMESPACE', order: ASC }   — the user sorted a column
 *   null                                 — no explicit sort; the backend decides
 *
 * The backend accepts exactly one sort pair, so one criterion is all that can
 * ever be expressed. The model deliberately cannot hold a second one: state the
 * backend ignores and the UI cannot honour is worse than no state at all.
 *
 * Why `null` is the DEFAULT — no `sortBy` is sent until a column is clicked
 * -----------------------------------------------------------------------
 * `OpenSearchQueryHelper.buildPrimarySort` resolves the ordering in this exact
 * order:
 *
 *   1. explicit `sortBy`      → that field, in the requested order
 *   2. else `queryText` present → `_score` DESC  (relevance / best match)
 *   3. else                     → `last_updated_at` DESC  (recency)
 *
 * Sending a `sortBy` on every request — as this module used to, by defaulting to
 * `LAST_UPDATED` desc — takes branch 1 unconditionally and makes branch 2
 * UNREACHABLE, so a keyword search came back ordered by recency instead of by
 * relevance. Omitting the pair entirely lets the backend pick: relevance for a
 * keyword search, recency for a browse. The browse default is therefore still
 * `last_updated_at` desc, now without clobbering relevance — the REQUEST just no
 * longer pins it. The resulting ordering is named to the user in the relevance
 * case only: when the last request carried no `sortBy` AND did carry query text,
 * `SearchResultsTable` renders "Sorted by relevance" as the Results header
 * description, inferred from the same resolution order above applied to a
 * snapshot of what that request transmitted. The recency (browse) default is left
 * unlabelled. That is a display-only label — it does not put a `sortBy` back on
 * the wire — and it drifts if this resolution order changes.
 *
 * Consequences a reviewer should know about:
 * - INVARIANT: every `SortField` has a clickable column, AND every `SortField`
 *   maps to a real backend `SortBy`. `SERVER_SORT_BY` and
 *   `SORT_FIELD_ITEM_PROPERTY` are TOTAL `Record`s, so breaking that is a compile
 *   error. A column the backend cannot sort (Locale — `SortBy` has no LOCALE
 *   value) declares no `sortingField`, so Cloudscape renders it as an inert
 *   header with no sort affordance at all.
 * - There is deliberately NO `LAST_UPDATED` field. It only ever existed to
 *   express the old default; it has no column (neither `SourceStringResult` nor
 *   `TranslationResult` carries a timestamp), so `toggleSortField` could never
 *   produce it and no header could ever indicate it. Recency is now reached via
 *   the unsorted state instead. Adding it back is only justified together with a
 *   real "Last updated" column, which first requires the Smithy model and the
 *   GraphQL result shapes to expose the timestamp.
 * - `SortBy.CREATED` exists in the contract but is not modelled here for the
 *   same reason — no timestamp in either payload.
 *
 * The three-state click cycle
 * ---------------------------
 * A plain click on the ALREADY-ACTIVE column cycles
 * `ascending → descending → unsorted`; clicking any OTHER column starts that
 * column ascending. The third click is what makes the unsorted state reachable
 * again after the user has sorted something — without it, and with no
 * "Last updated" column to click, the default ordering would be unreachable for
 * the rest of the session.
 *
 * `toggleSortField` is the SOLE authority on that cycle, including under
 * Cloudscape's native table sorting. Cloudscape's own single-column cycle is
 * two-state (ascending ⇄ descending): it reports `isDescending: false` for the
 * third activation, which is indistinguishable from "ascending again". So
 * `SearchResultsTable` reads only WHICH column was activated out of
 * `onSortingChange` and derives the next state here.
 *
 * The unsorted state needs no extra affordance to be discovered: it is reached by
 * continuing to click the column the user already sorted, and the header's
 * `aria-label` says so ("Activate to clear the sort").
 *
 * Why the client layer was removed
 * --------------------------------
 * This module used to hold an ordered criteria list where the PRIMARY criterion
 * went to the backend and the WHOLE list was then re-applied to the current page
 * by a client comparator. Because that comparator re-applied the primary too, any
 * disagreement with the backend's collation reordered rows the backend had
 * already ordered — and since the backend paginates with its own collation, that
 * made the visible sequence non-monotonic ACROSS pages: page 2 could start
 * "before" where page 1 ended. The comparator could only ever approximate the
 * server's collation (`localeCompare` is locale- and case-aware; the backend's
 * may not be), so the disagreement was structural, not a bug to fix. The layer
 * is gone; ordering now comes from one place.
 *
 * Because ordering is fully server-side, EVERY sort change must refetch from
 * page 1 and drop the cached `search_after` page tokens — a cursor is only valid
 * under the sort that produced it, and that includes transitions into and out of
 * the unsorted state. See `SearchResultsTable`'s sort effect.
 */
import { SortBy, SortOrder } from 'src/generated/graphql';

/**
 * Sortable column identity, at the UI level.
 *
 * A separate union from `SortBy` because these are UI-facing names, but every
 * member MUST have a `SERVER_SORT_BY` entry (the total `Record` below enforces
 * it) AND a clickable column. See the invariant in the module docs.
 */
export type SortField = 'STRING_ID' | 'NAMESPACE' | 'STRING_GROUP' | 'STATUS';

/** An explicit, user-chosen sort. */
export interface SortCriterion {
  readonly field: SortField;
  readonly order: SortOrder;
}

/**
 * The sort in effect, or `null` for "no explicit sort — let the backend decide"
 * (relevance when there is query text, recency when browsing).
 *
 * `null` rather than a discriminated `{ kind: 'DEFAULT' }` tag because every
 * consumer asks the same binary question — "is a column explicitly sorted?" —
 * which `sortState?.field === field` answers directly, and because `strict`
 * null-checking then forces every call site to handle the unsorted case instead
 * of relying on a tag check being remembered. It also survives the
 * `JSON.stringify` round-trip that saved searches go through, which `undefined`
 * would not: the `sortState` KEY must stay present in stored entries, since
 * `migrateSearchState` uses its presence as the schema-generation marker.
 */
export type SortState = SortCriterion | null;

/**
 * Every field that may legally appear in a sort state.
 *
 * Exactly the columns that render a sortable header — see the module invariant.
 * Used to validate sort state read back from storage.
 */
export const SORT_FIELDS: readonly SortField[] = [
  'STRING_ID',
  'NAMESPACE',
  'STRING_GROUP',
  'STATUS',
];

/** Resource ids for the column header labels, also used in the sort aria-labels. */
export const SORT_FIELD_LABEL_IDS: Record<SortField, string> = {
  STRING_ID: 'search-column-string-id',
  NAMESPACE: 'search-column-namespace',
  STRING_GROUP: 'search-column-string-group',
  STATUS: 'search-column-status',
};

/**
 * UI field → backend `SortBy`. TOTAL on purpose: a `SortField` with no backend
 * counterpart would be a column that appears sortable but cannot be. Adding one
 * is a compile error.
 */
const SERVER_SORT_BY: Record<SortField, SortBy> = {
  STRING_ID: SortBy.StringId,
  NAMESPACE: SortBy.Namespace,
  STRING_GROUP: SortBy.StringGroupId,
  STATUS: SortBy.Status,
};

/** Fields the backend only supports for SOURCE searches. */
export const SOURCE_ONLY_SORT_FIELDS: readonly SortField[] = ['STATUS'];

/**
 * UI field → the result-item property that column renders.
 *
 * This is the value handed to Cloudscape as the column definition's
 * `sortingField`, which is what makes the `<th>` render as sortable at all (see
 * `SearchResultsTable`). It doubles as the column's identity in
 * `onSortingChange`, which reports the clicked `sortingColumn` rather than a
 * column id — `sortFieldForItemProperty` below is the way back.
 *
 * Cloudscape only USES `sortingField` to reorder items inside the collection
 * hooks, which this table does not use, so nothing here sorts client-side; the
 * ordering still comes solely from the request. The names are nonetheless the
 * REAL item properties rather than convenient placeholders, so that if anyone
 * ever does wire up `useCollection` the fields point at the data they claim to.
 * `stringId`, `namespaceId` and `stringGroupId` exist on both
 * `SourceStringResult` and `TranslationResult`; `status` is source-only, which is
 * exactly where that column is rendered.
 */
export const SORT_FIELD_ITEM_PROPERTY: Record<SortField, string> = {
  STRING_ID: 'stringId',
  NAMESPACE: 'namespaceId',
  STRING_GROUP: 'stringGroupId',
  STATUS: 'status',
};

const ITEM_PROPERTY_SORT_FIELD = new Map<string, SortField>(
  SORT_FIELDS.map((field) => [SORT_FIELD_ITEM_PROPERTY[field], field]),
);

/**
 * Resolves the `sortingField` Cloudscape reports back on a header activation to
 * the UI sort field.
 *
 * Returns `null` for anything unrecognised — a column with no `sortingField`, or
 * one whose field is not a sortable criterion — so an unexpected event is
 * ignored rather than producing a sort state no header could indicate.
 */
export function sortFieldForItemProperty(
  itemProperty: string | undefined,
): SortField | null {
  return (
    (itemProperty !== undefined &&
      ITEM_PROPERTY_SORT_FIELD.get(itemProperty)) ||
    null
  );
}

/**
 * Default: NO explicit sort. The request carries neither `sortBy` nor
 * `sortOrder`, so the backend orders by relevance for a keyword search and by
 * recency for a browse — see the resolution order in the module docs.
 *
 * It is also trivially translation-safe (there is no field to be source-only),
 * so it can serve as the fallback for `removeSourceOnlySortField` and for
 * unusable stored sort state without the fallback itself ever needing a
 * fallback.
 */
export const DEFAULT_SORT_STATE: SortState = null;

/**
 * Applies a header activation to the sort state — the three-state cycle.
 *
 * - a DIFFERENT field (or currently unsorted) → sort by this field, ascending
 * - the ACTIVE field, ascending               → flip to descending
 * - the ACTIVE field, descending              → clear to unsorted
 *
 * Re-clicking the active column is therefore never a dead click, and the third
 * click is the only route back to the backend-decided ordering.
 */
export function toggleSortField(state: SortState, field: SortField): SortState {
  if (state === null || state.field !== field) {
    return { field, order: SortOrder.Asc };
  }
  return state.order === SortOrder.Asc
    ? { field, order: SortOrder.Desc }
    : DEFAULT_SORT_STATE;
}

/**
 * Translates the sort state into the backend's sort pair, or `{}` when there is
 * no explicit sort.
 *
 * Callers SPREAD the result into the request input so that an unsorted state
 * omits both KEYS rather than sending them as `undefined`.
 */
export function toServerSort(state: SortState): {
  sortBy?: SortBy;
  sortOrder?: SortOrder;
} {
  if (state === null) return {};
  return { sortBy: SERVER_SORT_BY[state.field], sortOrder: state.order };
}

/**
 * Replaces a criterion the backend rejects for TRANSLATION searches with the
 * default. An already-unsorted state has nothing to strip and is returned as-is,
 * and `DEFAULT_SORT_STATE` is itself translation-safe, so the fallback can never
 * reintroduce a rejected criterion.
 */
export function removeSourceOnlySortField(state: SortState): SortState {
  if (state === null) return state;
  return SOURCE_ONLY_SORT_FIELDS.includes(state.field)
    ? DEFAULT_SORT_STATE
    : state;
}

const SORT_FIELD_SET = new Set<string>(SORT_FIELDS);
const SORT_ORDER_SET = new Set<string>(Object.values(SortOrder));

/**
 * Type guard for sort state read back from untrusted storage (saved searches).
 *
 * Accepts `null` — the unsorted state is a legitimate persisted value — or a
 * single `{ field, order }` object with known values. Membership is checked
 * against `SORT_FIELDS`, so a field only becomes acceptable by also gaining a
 * clickable column and a `SERVER_SORT_BY` entry. Three classes of stored value
 * are rejected, and all three matter:
 * - a field this build does not offer (`LAST_UPDATED`, `CREATED`) — it has no
 *   clickable column, so restoring it would produce a sort the table cannot
 *   indicate and the user cannot undo. `STRING_GROUP` is NOT in this list any
 *   more: the backend gained `SortBy.StringGroupId`, the column is sortable
 *   again, so a persisted `STRING_GROUP` sort now round-trips;
 * - an unknown sort order, or any other non-`{ field, order }` value;
 * - an ARRAY — the previous multi-column shape. Its ordered criteria have no
 *   meaning now that only one criterion exists, and silently adopting element 0
 *   would resurrect a sort the user never chose under this model.
 *
 * A rejected sort does NOT discard the saved search: `migrateSearchState`
 * treats it as recoverable and resets to `DEFAULT_SORT_STATE` (i.e. unsorted),
 * keeping the query text, filters and mode.
 */
export function isValidSortState(value: unknown): value is SortState {
  if (value === null) return true;
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const { field, order } = value as Record<string, unknown>;
  return (
    typeof field === 'string' &&
    SORT_FIELD_SET.has(field) &&
    typeof order === 'string' &&
    SORT_ORDER_SET.has(order)
  );
}
